import React from 'react';
import { CheckoutConfig } from '../../../types';
import { STYLE_PRESETS, StylePreset } from './CheckoutStudioPresets';

const FONTS = ['Inter', 'Space Grotesk', 'Outfit', 'Plus Jakarta Sans', 'Montserrat', 'Roboto', 'monospace'];
const RADII = [
  { label: 'Sharp (0px)', value: '0px' },
  { label: 'Subtle (4px)', value: '4px' },
  { label: 'Modern (8px)', value: '8px' },
  { label: 'Rounded (16px)', value: '16px' },
  { label: 'Pill (999px)', value: '999px' },
];

interface VisualTabProps {
  config: CheckoutConfig;
  onUpdate: (partial: Partial<CheckoutConfig>) => void;
}

export const CheckoutStudioVisualTab: React.FC<VisualTabProps> = ({ config, onUpdate }) => {
  const handleApplyPreset = (preset: StylePreset) => {
    onUpdate({
      primaryColor: preset.primaryColor,
      backgroundColor: preset.backgroundColor,
      textColor: preset.textColor,
      fontFamily: preset.fontFamily,
      buttonRadius: preset.buttonRadius,
      inputStyle: preset.inputStyle,
    });
  };

  return (
    <div className="space-y-3.5 text-left">
      {/* 1-Click Theme Style Presets */}
      <div className="p-3 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-800 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-slate-800 dark:text-slate-200">1-Click Theme Style Presets</p>
          <span className="text-[10px] text-slate-400 font-semibold">Instant Palette Match</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {STYLE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => handleApplyPreset(preset)}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-indigo-500 text-left transition-all group shadow-sm"
            >
              <div className="flex items-center gap-1 mb-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: preset.primaryColor }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: preset.backgroundColor }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: preset.textColor }} />
              </div>
              <p className="text-[10px] font-bold text-slate-900 dark:text-white truncate group-hover:text-indigo-500">
                {preset.name}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Colors */}
      <div className="p-3 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-800 space-y-2.5">
        <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Color Palette &amp; Logo</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Primary', key: 'primaryColor', val: config.primaryColor || '#4f46e5' },
            { label: 'Background', key: 'backgroundColor', val: config.backgroundColor || '#ffffff' },
            { label: 'Text Color', key: 'textColor', val: config.textColor || '#0f172a' },
          ].map((c) => (
            <div key={c.key} className="space-y-1">
              <label className="text-[9px] font-bold text-slate-500 uppercase">{c.label}</label>
              <div className="flex items-center gap-1">
                <input
                  type="color"
                  value={c.val}
                  onChange={(e) => onUpdate({ [c.key]: e.target.value })}
                  className="w-6 h-6 rounded border border-slate-300 cursor-pointer p-0"
                />
                <input
                  type="text"
                  value={c.val}
                  onChange={(e) => onUpdate({ [c.key]: e.target.value })}
                  className="w-full text-[10px] font-mono px-1 py-0.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded"
                />
              </div>
            </div>
          ))}
        </div>
        <input
          type="text"
          value={config.logoUrl || ''}
          onChange={(e) => onUpdate({ logoUrl: e.target.value })}
          placeholder="Logo Image URL (e.g. https://.../logo.svg)"
          className="w-full text-xs px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none"
        />
      </div>

      {/* Typography & Geometry */}
      <div className="p-3 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-800 space-y-2.5">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase">Font Family</label>
            <select
              value={config.fontFamily || 'Inter'}
              onChange={(e) => onUpdate({ fontFamily: e.target.value })}
              className="w-full text-xs font-semibold px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
            >
              {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase">Corner Radius</label>
            <select
              value={config.buttonRadius || '8px'}
              onChange={(e) => onUpdate({ buttonRadius: e.target.value })}
              className="w-full text-xs font-semibold px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
            >
              {RADII.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => onUpdate({ inputStyle: 'outline' })}
            className={`flex-1 py-1 px-2 rounded-lg text-xs font-bold border transition-all ${
              config.inputStyle === 'outline'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300'
            }`}
          >
            Outlined Inputs
          </button>
          <button
            type="button"
            onClick={() => onUpdate({ inputStyle: 'solid' })}
            className={`flex-1 py-1 px-2 rounded-lg text-xs font-bold border transition-all ${
              config.inputStyle === 'solid'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300'
            }`}
          >
            Solid Inputs
          </button>
        </div>
      </div>

      {/* Copy */}
      <div className="p-3 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-800 space-y-2">
        <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Headings &amp; Button Copy</p>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            value={config.headerMain || ''}
            onChange={(e) => onUpdate({ headerMain: e.target.value })}
            placeholder="Header (e.g. Complete Order)"
            className="w-full text-xs px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none"
          />
          <input
            type="text"
            value={config.buttonText || ''}
            onChange={(e) => onUpdate({ buttonText: e.target.value })}
            placeholder="Button (e.g. Continue to Payment)"
            className="w-full text-xs px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none"
          />
        </div>
      </div>
    </div>
  );
};
